import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner, Badge, ListGroup, Alert } from 'react-bootstrap';

const VacationRequestForm = ({
    setSelectedDates,
    selectedDaysList,
    setSelectedDaysList,
    calculoVacaciones,
    requestVacation,
    saving
}) => {
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [totalDaysSelected, setTotalDaysSelected] = useState(0);

    /**
     * Días festivos oficiales en México con descanso obligatorio
     * Según la Ley Federal del Trabajo (Artículo 74)
     */
    const getFeriadosOficiales = (year) => {
        return {
            // Días fijos obligatorios
            [`${year}-01-01`]: { name: 'Año Nuevo', type: 'fijo', obligatorio: true },
            [`${year}-05-01`]: { name: 'Día del Trabajo', type: 'fijo', obligatorio: true },
            [`${year}-09-16`]: { name: 'Día de la Independencia', type: 'fijo', obligatorio: true },
            [`${year}-12-25`]: { name: 'Navidad', type: 'fijo', obligatorio: true },
            // Días móviles (primer lunes de cada mes correspondiente)
            ...getFirstMondayFeriados(year),
            // Días festivos especiales (no obligatorios por ley)
            ...getFeriadosEspeciales(year)
        };
    };

    /**
     * Días festivos especiales (no obligatorios por ley)
     */
    const getFeriadosEspeciales = (year) => {
        const feriados = {};
        
        // Jueves Santo (jueves antes del Domingo de Resurrección)
        const juevesSanto = getJuevesSanto(year);
        feriados[juevesSanto] = { 
            name: 'Jueves Santo', 
            type: 'religioso',
            obligatorio: false
        };
        
        // Viernes Santo (viernes antes del Domingo de Resurrección)
        const viernesSanto = getViernesSanto(year);
        feriados[viernesSanto] = { 
            name: 'Viernes Santo', 
            type: 'religioso',
            obligatorio: false
        };
        
        // Día de las Madres (10 de mayo)
        feriados[`${year}-05-10`] = { 
            name: 'Día de las Madres', 
            type: 'conmemorativo',
            obligatorio: false
        };
        
        return feriados;
    };

    /**
     * Calcula la fecha del Jueves Santo
     * (Jueves antes del Domingo de Pascua)
     */
    const getJuevesSanto = (year) => {
        // Algoritmo de Computus para calcular la Pascua
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        
        // Domingo de Pascua
        const domingoPascua = new Date(year, month - 1, day);
        
        // Jueves Santo es 3 días antes del Domingo de Pascua
        const juevesSanto = new Date(domingoPascua);
        juevesSanto.setDate(juevesSanto.getDate() - 3);
        
        return formatDateString(juevesSanto);
    };

    /**
     * Calcula la fecha del Viernes Santo
     * (Viernes antes del Domingo de Pascua)
     */
    const getViernesSanto = (year) => {
        // Algoritmo de Computus para calcular la Pascua
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        
        // Domingo de Pascua
        const domingoPascua = new Date(year, month - 1, day);
        
        // Viernes Santo es 2 días antes del Domingo de Pascua
        const viernesSanto = new Date(domingoPascua);
        viernesSanto.setDate(viernesSanto.getDate() - 2);
        
        return formatDateString(viernesSanto);
    };

    /**
     * Calcula los días festivos que se mueven al primer lunes
     * (algunas empresas usan este sistema)
     */
    const getFirstMondayFeriados = (year) => {
        const feriados = {};
        
        // Primer lunes de febrero (anteriormente 5 de febrero)
        const primerLunesFebrero = getPrimerLunes(year, 1);
        if (primerLunesFebrero !== `${year}-02-05`) {
            feriados[primerLunesFebrero] = { 
                name: 'Día de la Constitución (Lunes)', 
                type: 'movible',
                obligatorio: true
            };
        }

        // Tercer lunes de marzo (anteriormente 21 de marzo)
        const tercerLunesMarzo = getTercerLunes(year, 2);
        if (tercerLunesMarzo !== `${year}-03-21`) {
            feriados[tercerLunesMarzo] = { 
                name: 'Natalicio de Benito Juárez (Lunes)', 
                type: 'movible',
                obligatorio: true
            };
        }

        // Tercer lunes de noviembre (anteriormente 20 de noviembre)
        const tercerLunesNoviembre = getTercerLunes(year, 10);
        if (tercerLunesNoviembre !== `${year}-11-20`) {
            feriados[tercerLunesNoviembre] = { 
                name: 'Revolución Mexicana (Lunes)', 
                type: 'movible',
                obligatorio: true
            };
        }

        return feriados;
    };

    /**
     * Obtiene el primer lunes de un mes
     */
    const getPrimerLunes = (year, month) => {
        let date = new Date(year, month, 1);
        while (date.getDay() !== 1) { // 1 = Lunes
            date.setDate(date.getDate() + 1);
        }
        return formatDateString(date);
    };

    /**
     * Obtiene el tercer lunes de un mes
     */
    const getTercerLunes = (year, month) => {
        let date = new Date(year, month, 1);
        let lunesCount = 0;
        
        while (lunesCount < 3) {
            if (date.getDay() === 1) {
                lunesCount++;
                if (lunesCount === 3) break;
            }
            date.setDate(date.getDate() + 1);
        }
        
        return formatDateString(date);
    };

    /**
     * Formatea una fecha a string YYYY-MM-DD
     */
    const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    /**
     * Verifica si una fecha es día festivo oficial
     */
    const esFeriadoOficial = (dateString) => {
        const year = new Date(dateString).getFullYear();
        const feriados = getFeriadosOficiales(year);
        return feriados[dateString] || null;
    };

    /**
     * Sincronizar selectedDaysList con el padre cuando cambia
     */
    useEffect(() => {
        if (setSelectedDaysList) {
            // La lista interna ya se sincroniza automáticamente
        }
    }, [selectedDaysList]);

    /**
     * Crea una fecha local sin problemas de zona horaria
     */
    const createLocalDate = (dateString) => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    /**
     * Calcula todos los días entre dos fechas
     */
    const calculateDaysBetween = (start, end, excludeWeekends = false) => {
        if (!start || !end) return [];
        
        const startDate = createLocalDate(start);
        const endDate = createLocalDate(end);
        const days = [];

        // Validar que la fecha de fin sea posterior a la de inicio
        if (endDate < startDate) {
            return [];
        }

        let currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
            const dayOfWeek = currentDate.getDay();
            
            // Si excludeWeekends es true, saltar sábados (6) y domingos (0)
            if (!excludeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                
                const feriadoInfo = esFeriadoOficial(dateString);
                
                days.push({
                    date: new Date(currentDate),
                    dateString: dateString,
                    dayName: currentDate.toLocaleDateString('es-MX', { weekday: 'long' }),
                    dayNumber: currentDate.getDate(),
                    month: currentDate.toLocaleDateString('es-MX', { month: 'long' }),
                    year: currentDate.getFullYear(),
                    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
                    isFeriado: feriadoInfo !== null,
                    feriadoName: feriadoInfo?.name || null
                });
            }
            
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

    /**
     * Maneja el cambio en los inputs de fecha
     */
    const handleDateRangeChange = (e) => {
        const { name, value } = e.target;
        const newDateRange = {
            ...dateRange,
            [name]: value
        };
        setDateRange(newDateRange);

        // Si ambas fechas están seleccionadas, calcular los días
        if (newDateRange.startDate && newDateRange.endDate) {
            const days = calculateDaysBetween(newDateRange.startDate, newDateRange.endDate, false);
            setSelectedDaysList(days);
            setTotalDaysSelected(days.length);
            
            // Actualizar las fechas en el componente padre
            setSelectedDates({
                startDate: newDateRange.startDate,
                endDate: newDateRange.endDate
            });
        } else {
            setSelectedDaysList([]);
            setTotalDaysSelected(0);
        }
    };

    /**
     * Elimina un día específico de la lista
     */
    const removeDayFromList = (dateString) => {
        const updatedList = selectedDaysList.filter(day => day.dateString !== dateString);
        setSelectedDaysList(updatedList);
        setTotalDaysSelected(updatedList.length);

        // Si se eliminan todos los días, limpiar el rango
        if (updatedList.length === 0) {
            setDateRange({ startDate: '', endDate: '' });
            setSelectedDates({ startDate: '', endDate: '' });
        } else {
            // Actualizar el rango basado en los días restantes
            const dates = updatedList.map(d => d.dateString).sort();
            const newStartDate = dates[0];
            const newEndDate = dates[dates.length - 1];
            
            setDateRange({ startDate: newStartDate, endDate: newEndDate });
            setSelectedDates({ startDate: newStartDate, endDate: newEndDate });
        }
    };

    /**
     * Limpia toda la selección
     */
    const clearSelection = () => {
        setDateRange({ startDate: '', endDate: '' });
        setSelectedDaysList([]);
        setTotalDaysSelected(0);
        setSelectedDates({ startDate: '', endDate: '' });
    };

    /**
     * Validaciones antes de enviar
     */
    const canSubmit = () => {
        return (
            selectedDaysList.length > 0 &&
            totalDaysSelected <= calculoVacaciones.diasDisponibles &&
            !saving
        );
    };

    /**
     * Formatea la fecha para mostrar
     */
    const formatDisplayDate = (day) => {
        // Capitalizar primera letra del día y mes
        const dayName = day.dayName.charAt(0).toUpperCase() + day.dayName.slice(1);
        const month = day.month.charAt(0).toUpperCase() + day.month.slice(1);
        return `${dayName}, ${day.dayNumber} de ${month} ${day.year}`;
    };

    return (
        <div className="border rounded-3 p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                    <FontAwesomeIcon icon="calendar-plus" className="me-2" />
                    Solicitar Vacaciones
                </h6>
                {totalDaysSelected > 0 && (
                    <Badge bg="primary" pill>
                        {totalDaysSelected} d&iacute;a{totalDaysSelected !== 1 ? 's' : ''} seleccionado{totalDaysSelected !== 1 ? 's' : ''}
                    </Badge>
                )}
            </div>

            {/* Información de días disponibles */}
            <Alert variant={calculoVacaciones.diasDisponibles > 0 ? 'info' : 'warning'} className="py-2 mb-3">
                <div className="d-flex justify-content-between align-items-center">
                    <small>
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        D&iacute;as disponibles: <strong>{calculoVacaciones.diasDisponibles}</strong>
                    </small>
                    {totalDaysSelected > 0 && (
                        <small className="text-muted">
                            Restantes: <strong>{calculoVacaciones.diasDisponibles - totalDaysSelected}</strong>
                        </small>
                    )}
                </div>
            </Alert>

            {/* Selector de rango de fechas */}
            <div className="mb-3">
                <Form.Label className="fw-bold small">Selecciona el periodo de vacaciones</Form.Label>
                <div className="row g-2">
                    <div className="col-md-6">
                        <Form.Group>
                            <Form.Label className="small text-muted">Fecha de inicio</Form.Label>
                            <Form.Control
                                type="date"
                                name="startDate"
                                value={dateRange.startDate}
                                onChange={handleDateRangeChange}
                                disabled={saving}
                            />
                        </Form.Group>
                    </div>
                    <div className="col-md-6">
                        <Form.Group>
                            <Form.Label className="small text-muted">Fecha de fin</Form.Label>
                            <Form.Control
                                type="date"
                                name="endDate"
                                value={dateRange.endDate}
                                onChange={handleDateRangeChange}
                                min={dateRange.startDate}
                                disabled={saving || !dateRange.startDate}
                            />
                        </Form.Group>
                    </div>
                </div>
            </div>

            {/* Lista de días seleccionados */}
            {selectedDaysList.length > 0 && (
                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="fw-bold small mb-0">
                            D&iacute;as a solicitar ({totalDaysSelected})
                        </Form.Label>
                        <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={clearSelection}
                            disabled={saving}
                        >
                            <FontAwesomeIcon icon="times" className="me-1" />
                            Limpiar todo
                        </Button>
                    </div>
                    
                    <div 
                        className="border rounded"
                        style={{ 
                            maxHeight: '250px', 
                            overflowY: 'auto',
                            backgroundColor: '#f8f9fa'
                        }}
                    >
                        <ListGroup variant="flush">
                            {selectedDaysList.map((day, index) => (
                                <ListGroup.Item 
                                    key={day.dateString}
                                    className="d-flex justify-content-between align-items-center py-2 px-3"
                                    style={{ backgroundColor: 'transparent' }}
                                >
                                    <div className="d-flex align-items-center">
                                        <Badge 
                                            bg={
                                                day.isWeekend ? 'secondary' : 
                                                day.isFeriado ? (
                                                    esFeriadoOficial(day.dateString)?.obligatorio ? 'danger' : 'warning'
                                                ) : 'light'
                                            } 
                                            text={day.isWeekend || day.isFeriado ? 'white' : 'dark'}
                                            className="me-2"
                                            style={{ minWidth: '30px' }}
                                        >
                                            {index + 1}
                                        </Badge>
                                        <div className="d-flex flex-column">
                                            <span className="small">
                                                {formatDisplayDate(day)}
                                            </span>
                                            <div className="d-flex gap-1 mt-1">
                                                {day.isWeekend && (
                                                    <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>
                                                        Fin de semana
                                                    </Badge>
                                                )}
                                                {day.isFeriado && (
                                                    <Badge 
                                                        bg={esFeriadoOficial(day.dateString)?.obligatorio ? 'danger' : 'warning'}
                                                        style={{ fontSize: '0.65rem' }}
                                                    >
                                                        {esFeriadoOficial(day.dateString)?.obligatorio ? '🎉' : '🌟'} {day.feriadoName}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger p-0"
                                        onClick={() => removeDayFromList(day.dateString)}
                                        disabled={saving}
                                        title="Eliminar este d&iacute;a"
                                    >
                                        <FontAwesomeIcon icon="trash-alt" />
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </div>
                </div>
            )}

            {/* Validaciones y alertas */}
            {totalDaysSelected > calculoVacaciones.diasDisponibles && (
                <Alert variant="danger" className="py-2 mb-3">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                    <small>
                        Has seleccionado <strong>{totalDaysSelected}</strong> d&iacute;as, 
                        pero solo tienes <strong>{calculoVacaciones.diasDisponibles}</strong> disponibles.
                    </small>
                </Alert>
            )}

            {calculoVacaciones.diasDisponibles <= 0 && (
                <Alert variant="warning" className="py-2 mb-3">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                    <small>No tienes d&iacute;as de vacaciones disponibles</small>
                </Alert>
            )}

            {/* Botón de envío */}
            <div className="d-grid gap-2">
                <Button
                    variant="primary"
                    onClick={requestVacation}
                    disabled={!canSubmit()}
                    size="lg"
                >
                    {saving ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Enviando solicitud...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="paper-plane" className="me-2" />
                            Enviar Solicitud ({totalDaysSelected} d&iacute;a{totalDaysSelected !== 1 ? 's' : ''})
                        </>
                    )}
                </Button>
                
                {selectedDaysList.length > 0 && !saving && (
                    <small className="text-center text-muted">
                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                        Revisa los d&iacute;as seleccionados antes de enviar
                    </small>
                )}
            </div>
        </div>
    );
};

VacationRequestForm.propTypes = {
    selectedDates: PropTypes.shape({
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired
    }).isRequired,
    setSelectedDates: PropTypes.func.isRequired,
    selectedDaysList: PropTypes.array.isRequired,
    setSelectedDaysList: PropTypes.func.isRequired,
    calculoVacaciones: PropTypes.shape({
        diasDisponibles: PropTypes.number.isRequired
    }).isRequired,
    requestVacation: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired
};

export default VacationRequestForm;